import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import * as api from '../api';
import { Icon } from './Icon';
import { Avatar, Button, IconButton, Input } from './ui';
import { queryKeys } from '../state/queries';
import { useActiveMemberId } from '../state/activeMember';
import { toast } from '../state/ui';

/**
 * In-context discussion on an event — leave a note ("did you mean the 8th?") and the household
 * gets pinged, so the conversation lives on the thing instead of in a lost text thread. "Got it"
 * is a one-tap acknowledgement (a check, not an emoji pile).
 */
export function EventComments({ eventId }: { eventId: string }) {
  const qc = useQueryClient();
  const activeId = useActiveMemberId();
  const key = ['event-comments', eventId];
  const [text, setText] = useState('');

  const { data: comments = [] } = useQuery({ queryKey: key, queryFn: () => api.getEventComments(eventId) });
  const { data: members = [] } = useQuery({ queryKey: queryKeys.members, queryFn: api.getMembers });

  const refresh = () => qc.invalidateQueries({ queryKey: key });

  const add = useMutation({
    mutationFn: (c: { kind?: 'comment' | 'ack'; body?: string }) =>
      api.addEventComment(eventId, { ...c, authorId: activeId }),
    onSuccess: () => {
      setText('');
      void refresh();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteEventComment(eventId, id),
    onSuccess: refresh,
  });

  const send = (): void => {
    if (text.trim()) add.mutate({ kind: 'comment', body: text.trim() });
  };

  return (
    <div className="flex flex-col gap-2.5 border-t border-border-subtle pt-4">
      <div className="flex items-center gap-2">
        <Icon name="message" size={14} className="text-muted" />
        <span className="eyebrow">Comments</span>
      </div>

      {comments.length > 0 && (
        <ul className="flex flex-col gap-2.5">
          {comments.map((c) => {
            const author = members.find((m) => m.id === c.authorId);
            return (
              <li key={c.id} className="group flex items-start gap-2.5">
                <Avatar
                  name={author?.name ?? '?'}
                  color={author?.color ?? '#5a6075'}
                  photoUrl={author?.avatarUrl}
                  size={26}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[13px] text-primary">{author?.name ?? 'Someone'}</span>
                    <span className="tabular font-mono text-[11px] text-muted">
                      {DateTime.fromISO(c.createdAt).toRelative({ style: 'short' })}
                    </span>
                  </div>
                  {c.kind === 'ack' ? (
                    <span className="flex items-center gap-1 text-[13px] text-success">
                      <Icon name="check" size={12} /> got it
                    </span>
                  ) : (
                    <p className="whitespace-pre-wrap break-words text-[13.5px] text-secondary">{c.body}</p>
                  )}
                </div>
                <IconButton
                  icon="trash"
                  label="Delete comment"
                  size="sm"
                  onClick={() => remove.mutate(c.id)}
                  className="opacity-0 transition-opacity group-hover:opacity-100 focus:opacity-100"
                />
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-center gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              send();
            }
          }}
          placeholder="Add a comment…"
          enterKeyHint="send"
          autoCapitalize="sentences"
        />
        <Button
          variant="secondary"
          size="sm"
          icon="check"
          disabled={add.isPending}
          onClick={() => add.mutate({ kind: 'ack' })}
        >
          Got it
        </Button>
        <Button variant="primary" size="sm" disabled={!text.trim() || add.isPending} onClick={send}>
          Send
        </Button>
      </div>
    </div>
  );
}
